// src/dates.ts
import { format, parseISO, differenceInMinutes, isWeekend } from "date-fns";
function formatBusinessDate(date) {
  return format(parseISO(date), "MMM d, yyyy");
}
function formatTimestamp(timestamp) {
  return format(parseISO(timestamp), "MMM d, yyyy h:mm a");
}
function formatDuration(minutes) {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
}
function isBusinessDay(date) {
  const d = typeof date === "string" ? parseISO(date) : date;
  return !isWeekend(d);
}

// src/auth.tsx
import * as React from "react";
import { jsx } from "react/jsx-runtime";
var AuthContext = React.createContext(null);
function AuthProvider({ children }) {
  const [user, setUser] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/v1/me");
        if (response.ok) {
          const data = await response.json();
          setUser(data.data);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, []);
  const hasRole = React.useCallback(
    (roles) => {
      if (!user) return false;
      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(user.role);
    },
    [user]
  );
  const logout = React.useCallback(() => {
    window.location.href = "/.cloudflare/logout";
  }, []);
  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    hasRole,
    logout
  };
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// src/api/client.ts
var API_BASE_URL = "/api/v1";
function useApiClient(options = {}) {
  const baseUrl = options.baseUrl ?? API_BASE_URL;
  const request = async (endpoint, { params, ...init } = {}) => {
    const url = new URL(endpoint, window.location.origin);
    url.pathname = `${baseUrl}${endpoint}`;
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== void 0) {
          url.searchParams.append(key, String(value));
        }
      });
    }
    const response = await fetch(url.toString(), {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...init.headers
      },
      credentials: "include"
      // Include Cloudflare Access cookies
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: { code: "UNKNOWN_ERROR", message: response.statusText }
      }));
      throw new ApiError(
        error.error?.code ?? "UNKNOWN_ERROR",
        error.error?.message ?? response.statusText,
        response.status
      );
    }
    return response.json();
  };
  return {
    get: (endpoint, options2) => request(endpoint, { ...options2, method: "GET" }),
    post: (endpoint, body, options2) => request(endpoint, { ...options2, method: "POST", body: JSON.stringify(body) }),
    patch: (endpoint, body, options2) => request(endpoint, { ...options2, method: "PATCH", body: JSON.stringify(body) }),
    delete: (endpoint, options2) => request(endpoint, { ...options2, method: "DELETE" })
  };
}
var ApiError = class extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = "ApiError";
  }
};
export {
  API_BASE_URL,
  AuthProvider,
  formatBusinessDate,
  formatDuration,
  formatTimestamp,
  isBusinessDay,
  useApiClient,
  useAuth
};
